import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { formatEther, isAddress } from "viem";
import dayjs from "dayjs";
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** 首页与弹窗中美元计价展示：在已格式化的数字前加 `$`，避免重复。 */
export function formatUsdPrefix(formattedAmount: string): string {
  const s = formattedAmount.trim();
  if (!s) return "$0";
  if (s.startsWith("$")) return s;
  return `$${s}`;
}

/**
 * 格式化数字为千分位格式
 * @param value 要格式化的数字
 * @param decimals 保留的小数位数，默认为 2
 * @returns 格式化后的字符串，例如：1,234.56
 */
export function formatAmount(
  value: number | string,
  decimals: number = 2
): string {
  const num = typeof value === "string" ? parseFloat(value) : value;

  if (isNaN(num)) {
    return "0";
  }

  // 使用 toLocaleString 进行千分位格式化
  return num.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * 格式化地址为脱敏格式
 * @param address 完整的地址
 * @param prefixLength 前缀保留长度，默认为 4
 * @param suffixLength 后缀保留长度，默认为 6
 * @returns 格式化后的地址，例如：0x12...abcdef
 */
export function formatAddress(
  address: string,
  prefixLength: number = 4,
  suffixLength: number = 3
): string {
  if (!address) {
    return "";
  }

  if (address.length <= prefixLength + suffixLength) {
    return address;
  }

  return `${address.slice(0, prefixLength)}...${address.slice(-suffixLength)}`;
}

/**
 * 格式化数字输入，限制最多两位小数
 * @param value 输入值
 * @param maxValue 最大值（可选），如果超过则自动设置为最大值
 * @returns 格式化后的值
 */
export function formatNumberInput(
  value: string,
  maxValue?: number
): [string, boolean] {
  // 允许空值
  if (value === "") {
    return ["", false];
  }

  // 只允许数字和小数点
  let formatted = value.replace(/[^0-9.]/g, "");

  // 限制只能有一个小数点
  const parts = formatted.split(".");
  if (parts.length > 2) {
    formatted = parts[0] + "." + parts.slice(1).join("");
  }

  // 限制小数位最多2位
  if (parts.length === 2 && parts[1].length > 2) {
    formatted = parts[0] + "." + parts[1].slice(0, 2);
  }
  let isGtMaxValue = false;
  // 检查是否超过最大值
  if (maxValue !== undefined) {
    const numValue = parseFloat(formatted);
    if (!isNaN(numValue) && numValue > maxValue) {
      isGtMaxValue = true;
      formatted = maxValue.toFixed(2);
    }
  }

  return [formatted, isGtMaxValue];
}

/**
 * 验证地址格式的函数
 * @param address 要验证的地址
 * @returns 是否为有效的 EVM 地址
 */
export const isValidAddress = (address: string): boolean => {
  if (!address || typeof address !== "string") {
    return false;
  }
  const trimmedAddress = address.trim();
  return isAddress(trimmedAddress);
};

/**
 * Ether to formatEther 1000000000000000000 to 1
 * @param value 要格式化的数字 18位精度
 * @param decimals 保留的小数位数，默认为 2（截断，不四舍五入）
 */
export function formatEtherWithDecimals(
  value: string | bigint | number,
  decimals: number = 2
): string {
  const amountStr = formatEther(BigInt(value || 0));
  const [integerPart, decimalPart = ""] = amountStr.split(".");

  // 截断小数部分到指定长度
  const truncatedDecimal = decimalPart.slice(0, decimals);

  // 如果不需要小数位，直接返回整数部分
  if (decimals === 0) {
    return integerPart;
  }

  // 如果小数部分不足，补0
  const paddedDecimal = truncatedDecimal.padEnd(decimals, "0");

  return `${integerPart}.${paddedDecimal}`;
}

/**
 * 将数值向上进位到指定小数位（进一，非四舍五入、非截断）。
 *
 * 适用于展示或额度计算等需要「保守高估」的场景：例如保证金余额展示、
 * 10000U 上限剩余额度计算时，避免截断低估余额导致前端算出的可转入金额偏大、
 * 提交后被后端拒绝。
 *
 * @param value 原始数值（number 或数字字符串）
 * @param decimals 目标小数位数，默认 2
 * @returns 进位后的 number
 *
 * @example ceilToDecimals(9999.991, 2) // 10000
 * @example ceilToDecimals(1.001, 2) // 1.01
 * @example ceilToDecimals(1.0, 2) // 1
 */
export function ceilToDecimals(
  value: number | string,
  decimals: number = 2
): number {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (!Number.isFinite(num)) return 0;
  if (num <= 0) return 0;

  const factor = 10 ** decimals;
  // 用字符串拆分整数/小数，减少浮点乘法误差（如 1.005 * 100）
  const str = num.toString();
  const [intPart = "0", decPart = ""] = str.split(".");
  const paddedDec = decPart.padEnd(decimals + 1, "0").slice(0, decimals + 1);
  const needsCeil = paddedDec.length > decimals && paddedDec[decimals] !== "0";
  const baseDec = paddedDec.slice(0, decimals);
  let int = BigInt(intPart || "0");
  let dec = BigInt(baseDec.padEnd(decimals, "0") || "0");

  if (needsCeil) {
    dec += 1n;
    const decMod = 10n ** BigInt(decimals);
    if (dec >= decMod) {
      int += 1n;
      dec -= decMod;
    }
  }

  const decStr = dec.toString().padStart(decimals, "0");
  return Number(`${int}.${decStr}`);
}

/**
 * 将链上 wei 金额向上进位到指定展示小数位（默认 2 位），返回进位后的 wei。
 *
 * 在 18 位 ether 精度下，等价于按「最小展示单位」对 wei 做整数向上取整，
 * 全程 bigint 运算，避免 Number 精度问题。
 *
 * @param wei 链上原始 wei（如 USDT balanceU）
 * @param decimals 展示小数位数，默认 2
 * @returns 进位后的 wei
 *
 * @example ceilWeiToDecimals(parseEther("9999.999"), 2) // parseEther("10000")
 */
export function ceilWeiToDecimals(wei: bigint, decimals: number = 2): bigint {
  if (wei <= 0n) return 0n;
  const etherDecimals = 18;
  if (decimals >= etherDecimals) return wei;
  const factor = 10n ** BigInt(etherDecimals - decimals);
  return ((wei + factor - 1n) / factor) * factor;
}

/**
 * 将 wei 按指定小数位向上进位后，转为 UI 展示用 number（固定小数位）。
 */
export function ceilWeiToDisplayNumber(
  wei: bigint,
  decimals: number = 2
): number {
  return Number(
    formatEtherWithDecimals(ceilWeiToDecimals(wei, decimals), decimals)
  );
}

/**
 * 截断数字并保留指定小数位数，自动去除末尾多余的0
 * @param value 要格式化的数字或字符串
 * @param maxDecimals 最大保留的小数位数，默认为 6
 * @returns 格式化后的字符串，例如：0.009999999800000003 -> "0.009999"
 */
export function formatNumberTruncate(
  value: number | string,
  maxDecimals: number = 2
): string {
  // 转换为数字
  const num = typeof value === "string" ? parseFloat(value) : value;

  // 处理无效值
  if (isNaN(num) || !isFinite(num)) {
    return "0";
  }

  // 处理整数
  if (Number.isInteger(num)) {
    return num.toString();
  }

  // 转换为字符串，保留足够的小数位
  const str = num.toString();
  const [integerPart, decimalPart = ""] = str.split(".");

  // 截断小数部分到指定长度
  const truncatedDecimal = decimalPart.slice(0, maxDecimals);

  // 去除末尾的0
  const trimmedDecimal = truncatedDecimal.replace(/0+$/, "");

  // 如果小数部分为空，只返回整数部分
  if (!trimmedDecimal) {
    return integerPart;
  }

  return `${integerPart}.${trimmedDecimal}`;
}

/**
 * 将数字格式化为 K/M/B 缩写，保留两位小数（截断，不四舍五入）
 * @param value 要格式化的数字或字符串
 * @returns 格式化后的字符串，例如：1234567 → "1.23M"，999 → "999.00"
 */
export function formatKMBTruncate(
  value: number | string,
  fractionDigits: number = 2
): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num) || !isFinite(num)) {
    return fractionDigits === 0 ? "0" : "0.00";
  }

  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";

  let divisor = 1;
  let suffix = "";
  if (abs >= 1e9) {
    divisor = 1e9;
    suffix = "B";
  } else if (abs >= 1e6) {
    divisor = 1e6;
    suffix = "M";
  } else if (abs >= 1e3) {
    divisor = 1e3;
    suffix = "K";
  }

  const scaled = abs / divisor;

  if (fractionDigits === 0) {
    if (divisor === 1) {
      return `${sign}${formatThousands(Math.floor(abs), 0)}`;
    }
    return `${sign}${Math.floor(scaled)}${suffix}`;
  }

  const truncated = Math.floor(scaled * 100) / 100;
  return `${sign}${truncated.toFixed(2)}${suffix}`;
}

/**
 * 将数字格式化为 K/M/B 缩写，保留两位小数（四舍五入）
 * @param value 要格式化的数字或字符串
 * @returns 格式化后的字符串，例如：1235567 → "1.24M"，999 → "999.00"
 */
export function formatKMBRound(value: number | string): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num) || !isFinite(num)) return "0.00";

  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";

  let divisor = 1;
  let suffix = "";
  if (abs >= 1e9) {
    divisor = 1e9;
    suffix = "B";
  } else if (abs >= 1e6) {
    divisor = 1e6;
    suffix = "M";
  } else if (abs >= 1e3) {
    divisor = 1e3;
    suffix = "K";
  }

  const scaled = abs / divisor;
  return `${sign}${scaled.toFixed(2)}${suffix}`;
}

/**
 * 格式化数字为千分位分隔格式，保留指定小数位（截断，不四舍五入）。默认 2 位。
 */
export function formatThousands(
  value: number | string,
  fractionDigits: number = 2
): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num) || !isFinite(num)) {
    return fractionDigits === 0 ? "0" : `0.${"0".repeat(fractionDigits)}`;
  }

  const sign = num < 0 ? "-" : "";
  const abs = Math.abs(num);

  if (fractionDigits === 0) {
    const truncated = Math.floor(abs);
    const formatted = String(truncated).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return `${sign}${formatted}`;
  }

  const factor = 10 ** fractionDigits;
  const truncated = Math.floor(abs * factor) / factor;
  const [intPart, decPart = ""] = truncated.toFixed(fractionDigits).split(".");
  const formatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${sign}${formatted}.${decPart}`;
}
/** Unicode 下标数字 ₀₁₂₃₄₅₆₇₈₉ */
const SUBSCRIPT_DIGITS =
  "\u2080\u2081\u2082\u2083\u2084\u2085\u2086\u2087\u2088\u2089";

function toSubscript(n: number): string {
  return String(n).replace(/\d/g, d => SUBSCRIPT_DIGITS[Number(d)]);
}

/**
 * 格式化极小小数：0.0 + 下标(前导零个数) + 有效数字
 * 例如 "0.0000006680887601244178" => "0.0₆668088"
 * @param value 数字或字符串
 * @param significantDigits 有效数字位数，默认 6
 * @returns 格式化后的字符串，如 "0.0₅66908"
 */
export function formatTinyDecimal(
  value: string | number,
  significantDigits: number = 2
): string {
  const raw =
    typeof value === "number" ? value.toString() : String(value ?? "").trim();
  if (!raw || raw === "0") return "0";

  const num = Number(raw);
  if (isNaN(num)) return raw;
  if (num >= 1 || num <= -1) return raw;

  const str = raw.startsWith("0.") ? raw : num.toString();
  const dotIndex = str.indexOf(".");
  if (dotIndex === -1) return str;

  const decPart = str.slice(dotIndex + 1);
  const firstNonZero = decPart.search(/[^0]/);

  if (firstNonZero === -1)
    return "0.0" + toSubscript(decPart.length) + (decPart.slice(0, 6) || "0");

  const zeroCount = firstNonZero;
  const significantPart = decPart.slice(
    firstNonZero,
    firstNonZero + significantDigits
  );

  return `0.0${toSubscript(zeroCount)}${significantPart}`;
}
/**
 * 弹窗内代币数量统一格式化（FUSD / TSK / USDT 通用）：
 *
 * 1. 先截断到 2 位小数（不四舍五入）—— 与项目其他位置 `formatEtherWithDecimals(wei, 2)` 风格一致
 * 2. 再做 K / M / B 缩写 —— 复用 `formatKMBTruncate`
 *
 * 输入约定：
 * - `number / string`：已转为**可读数值**的代币数量（弹窗内现状），直接走 KMB
 * - `bigint`：链上原始 **wei**，先 `formatEtherWithDecimals(wei, 2)` 转可读再走 KMB
 *
 * 仅在 `SignalFeeConfirmDialog` 内部使用，统一三种 token 数字展示风格。
 *
 * @example formatTokenAmount(12.3456)                 // "12.34"
 * @example formatTokenAmount(1234.56)                 // "1.23K"
 * @example formatTokenAmount(12_345_678)              // "12.34M"
 * @example formatTokenAmount(12_345_600_000_000_000_000n) // "12.34"（按 1e18 wei 转换）
 */
export const formatTokenAmountToKMB = (
  value: number | string | bigint
): string => {
  if (typeof value === "bigint") {
    return formatKMBTruncate(formatEtherWithDecimals(value, 2), 2);
  }
  return formatKMBTruncate(value, 2);
};
